import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validateProductOptions } from '../../apis/products';
import strings from '../../constants/strings.constants';
import URL_QUERY_KEYS from '../../constants/urlQueryKeys.constants';
import useAlertStore from '../../store/useAlertStore';
import useAuthStore from '../../store/useAuthStore';
import useLanguageCodeStore, {
  LanguageCode,
} from '../../store/useLanguageCodeStore';
import useProductOptionsStore from '../../store/useProductOptionsStore';
import { ProductOption } from '../../apis/models/products/res/ProductOption';
import {
  getProductOptionSnapshotKey,
  mergeValidatedProductOptions,
  parseProductOptionValidationRows,
  toProductOptionValidationItems,
} from '../../utils/product-option-validation';

export type SelectedProductOptionsValidationResult =
  | { status: 'unchanged'; productOptions: ProductOption[] }
  | { status: 'changed'; productOptions: ProductOption[] }
  | { status: 'stale' };

type ValidationContext = {
  partnerCode: string;
  languageCode: LanguageCode;
};

function readValidationContext(): ValidationContext | undefined {
  const partnerCode = useAuthStore.getState().partner?.partnerCode;
  if (!partnerCode) return undefined;
  return {
    partnerCode,
    languageCode: useLanguageCodeStore.getState().languageCode,
  };
}

function getContextKey(context: ValidationContext) {
  return `${context.partnerCode}|${context.languageCode}`;
}

/** 요청 시작 시점의 파트너·언어·선택 스냅샷이 그대로인지 확인한다. */
function isContextCurrent(context: ValidationContext, snapshotKey: string) {
  const current = readValidationContext();
  return (
    !!current &&
    getContextKey(current) === getContextKey(context) &&
    getProductOptionSnapshotKey(
      useProductOptionsStore.getState().productOptions,
    ) === snapshotKey
  );
}

// 같은 컨텍스트·선택 스냅샷의 백그라운드 검증은 진행 중 요청을 공유한다. (시간 기반 제한은 두지 않는다)
const inFlightValidations = new Map<
  string,
  Promise<SelectedProductOptionsValidationResult>
>();

async function executeValidation(
  context: ValidationContext,
  productOptions: ProductOption[],
  snapshotKey: string,
): Promise<SelectedProductOptionsValidationResult> {
  const items = toProductOptionValidationItems(productOptions);
  const response = await validateProductOptions(context.partnerCode, {
    languageCode:
      context.languageCode === LanguageCode.ko
        ? undefined
        : context.languageCode,
    items,
  });
  const rows = parseProductOptionValidationRows(response, items);

  if (!isContextCurrent(context, snapshotKey)) {
    return { status: 'stale' };
  }

  const { productOptions: latest, replaceProductOptions } =
    useProductOptionsStore.getState();
  const merged = mergeValidatedProductOptions(latest, rows);

  if (merged.changed || merged.refreshed) {
    replaceProductOptions(merged.productOptions);
  }
  if (merged.changed) {
    const messages: string[] = [];
    if (merged.removedCount > 0) {
      messages.push(strings.productOptionRemovedNotice);
    }
    if (merged.updatedCount > 0) {
      messages.push(strings.productOptionUpdatedNotice);
    }
    useAlertStore.getState().showAlert(messages.join(' '));
    return { status: 'changed', productOptions: merged.productOptions };
  }
  return { status: 'unchanged', productOptions: merged.productOptions };
}

/**
 * 현재 선택을 서버 판정으로 검증한다. 실패는 throw한다.
 * - shareInFlight: 백그라운드 트리거는 같은 키의 진행 중 요청을 공유한다.
 *   예약 직전 검증은 shareInFlight=false로 항상 새 요청을 보낸다.
 */
export function runSelectedProductOptionsValidation({
  shareInFlight,
}: {
  shareInFlight: boolean;
}): Promise<SelectedProductOptionsValidationResult> {
  const context = readValidationContext();
  const productOptions = useProductOptionsStore.getState().productOptions;
  if (!context) return Promise.resolve({ status: 'stale' });
  if (productOptions.length === 0) {
    return Promise.resolve({ status: 'unchanged', productOptions });
  }

  const snapshotKey = getProductOptionSnapshotKey(productOptions);
  const dedupeKey = `${getContextKey(context)}|${snapshotKey}`;
  if (shareInFlight) {
    const existing = inFlightValidations.get(dedupeKey);
    if (existing) return existing;
  }

  const promise: Promise<SelectedProductOptionsValidationResult> =
    executeValidation(context, productOptions, snapshotKey).finally(() => {
      if (inFlightValidations.get(dedupeKey) === promise) {
        inFlightValidations.delete(dedupeKey);
      }
    });
  if (shareInFlight) inFlightValidations.set(dedupeKey, promise);
  return promise;
}

/**
 * 예약 직전 검증. 요청마다 새 검증을 수행하고, 검증된 단일 스냅샷만 제출에 사용한다.
 * - 'unchanged': productOptions를 요청 body·금액·완료 화면에 사용한다.
 * - 'changed': 변경 안내는 이미 표시되었다. 제출하지 않는다.
 * - 'stale': 검증 중 선택이 바뀌었다. 제출하지 않는다.
 * - 검증 요청 실패는 throw한다. 호출 측이 기존 오류 안내로 제출을 막는다.
 */
export function validateSelectedProductOptionsForSubmit() {
  return runSelectedProductOptionsValidation({ shareInFlight: false });
}

/**
 * 예약 페이지 진입·탭 복귀·창 포커스 시 선택 옵션을 백그라운드 검증한다.
 * 백그라운드 실패는 조용히 무시한다(예약 직전 검증이 다시 막는다).
 * 임시 데이터(isTempData) 흐름과 선택이 없을 때는 요청하지 않는다.
 */
export function useSelectedProductOptionsSync() {
  const [searchParams] = useSearchParams();
  const partnerCode = useAuthStore((state) => state.partner?.partnerCode);
  const languageCode = useLanguageCodeStore((state) => state.languageCode);
  const isTempData = searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true';
  const enabled = !!partnerCode && !isTempData;

  useEffect(() => {
    if (!enabled) return;

    const revalidate = () => {
      void runSelectedProductOptionsValidation({ shareInFlight: true }).catch(
        () => undefined,
      );
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    revalidate();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', revalidate);
    };
  }, [enabled, partnerCode, languageCode]);
}
